import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SetAlertType } from "schemas/functions";

import StripeCheckout from "components/StripeCheckout";
import SubscriptionButton from "features/Settings/SubscriptionButton";
import { DISPLAYED_ONBOARDING_PAYWALL } from "utils/localStorage";
import { trackEvent } from "utils/tracking";

import OnboardingPaywall from "./OnboardingPaywall";
import { DisplayVaOnly, PaidAccessFeatures, getAllPlans } from "./Plan/helpers";
import Subscription from "./Subscription";
import UpgradeFeatureDescription from "./UpgradeFeatureDescription";

interface IProps {
  open: boolean;
  onClose: () => void;
  setAlert: SetAlertType;
}

export function UpgradeDialog({ open, onClose, setAlert }: IProps) {
  const { currentOrg } = useContext(OrganizationUserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    upgradeDialogSource,
    subscription,
    tab,
    openCheckoutDialog,
    setOpenCheckoutDialog,
  } = useContext(SubscriptionContext);

  const close = () => {
    if (openCheckoutDialog) {
      setOpenCheckoutDialog(null);
      searchParams.set("checkout", "failed");
      setSearchParams(searchParams);
    } else {
      setTimeout(() => {
        searchParams.delete("checkout");
        setSearchParams(searchParams);
      }, 500);
    }
    if (upgradeDialogSource === "Onboarding Paywall") {
      trackEvent("Onboarding Paywall - Close Button Clicked");
      localStorage.setItem(
        `${DISPLAYED_ONBOARDING_PAYWALL}-${currentOrg?.id}`,
        "true",
      );
    }

    onClose();
  };

  useEffect(() => {
    if (open) {
      trackEvent("Shown Paywall Dialog", {
        Reason: upgradeDialogSource,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getMaxWidth = () => {
    if (openCheckoutDialog?.plan) {
      return "lg";
    } else if (DisplayVaOnly?.includes(upgradeDialogSource)) {
      return "sm";
    }
    const totalPlansDisplayed = getAllPlans(
      tab,
      upgradeDialogSource,
      subscription?.subscription?.planName,
    )?.length;
    if (totalPlansDisplayed === 1) return "sm";
    if (totalPlansDisplayed === 2) return "md";
    return "lg";
  };

  const renderDialog = () => {
    if (!openCheckoutDialog?.plan) {
      if (upgradeDialogSource === "Onboarding Paywall") {
        return <OnboardingPaywall />;
      }
      return (
        <>
          {searchParams.get("checkout") !== "failed" && (
            <>
              <DialogTitle variant="h4">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {PaidAccessFeatures.includes(upgradeDialogSource) ? (
                    <>This is a feature for Paid Users 🔐</>
                  ) : (
                    "Pick Your Subscription Plan"
                  )}
                </Box>
              </DialogTitle>
              <DialogContent>
                <IconButton
                  aria-label="close"
                  onClick={close}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    width: 30,
                    height: 30,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <Box component="i" className="fa-regular fa-xmark" />
                </IconButton>

                {upgradeDialogSource && <UpgradeFeatureDescription />}

                <Grid sx={{ mb: 3 }} container justifyContent="center">
                  <SubscriptionButton />
                </Grid>
                <Subscription upgradeDialogSource={upgradeDialogSource} />
              </DialogContent>
            </>
          )}
        </>
      );
    } else {
      return <StripeCheckout openCheckoutDialog={openCheckoutDialog} />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth={getMaxWidth()}
      fullScreen={isMobileScreen}
      fullWidth
    >
      {renderDialog()}
    </Dialog>
  );
}
